/**
 * Event header bar for the public viewer (extracted verbatim from
 * BrowseStateView during the MVP three-mode rebuild — load-bearing,
 * survives per handover §3).
 */

import React from 'react';
import { Share2 } from 'lucide-react';

import { EventRow } from '../../services/fleet';

export const eventLogo = (name: string): string => {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || 'EV';
};

export const dayLabel = (event: EventRow): string => {
  const parts: string[] = [];
  if (event.location) parts.push(event.location);
  if (event.type) parts.push(event.type);
  return parts.join(' · ') || '—';
};

export const EventHeader: React.FC<{
  event: EventRow;
  watching: number;
  onShare: () => void;
  replay?: boolean;
  /** Theater mode shows the followed crew inline (mockup .evt-driver-inline). */
  driverInline?: { number: string; name: string } | null;
  /** Center slot (lg+) — the StageStrip lives here on desktop so the
   *  header's dead middle earns its keep and the map loses a floating
   *  pill. Hidden below lg (phones keep the panel-skin strip). */
  centerSlot?: React.ReactNode;
}> = ({ event, watching, onShare, replay = false, driverInline = null, centerSlot = null }) => (
  // Compact on phones (mockup .m-evt-h: 24px logo, name-only line) —
  // full two-line header from sm up.
  // ONE line — name, location/type and the state chip sit inline
  // (the stacked two-line block made the bar taller than its content).
  <header className="flex items-center gap-2 border-b border-border-subtle bg-bg-surface px-3.5 py-2 sm:gap-3 sm:px-5">
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent/15 font-mono text-[11px] font-bold text-accent sm:h-7 sm:w-7 sm:text-xs">
      {eventLogo(event.name)}
    </div>
    <div className="flex min-w-0 items-baseline gap-2">
      <div className="truncate text-xs font-semibold text-fg sm:text-sm">{event.name}</div>
      <div className="hidden truncate text-xs text-fg-secondary sm:block">{dayLabel(event)}</div>
    </div>
    {replay ? (
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-bg-elevated px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-fg-secondary">
        Ended · Replay
      </span>
    ) : (
      // Red, matching the video badge's LIVE — one color for "live"
      // everywhere (was orange here, red on the player).
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[var(--status-error-bg)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-status-error">
        <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-status-error" />
        Live
      </span>
    )}
    {driverInline && (
      <span className="ml-1 hidden items-center gap-1.5 border-l border-border-subtle pl-3 sm:inline-flex">
        <span className="font-mono text-sm font-bold text-accent">{driverInline.number}</span>
        <span className="text-xs font-semibold text-fg">{driverInline.name}</span>
      </span>
    )}
    <div className="flex min-w-0 flex-1 items-center justify-center">
      {centerSlot && <div className="hidden min-w-0 lg:block">{centerSlot}</div>}
    </div>
    <span className="hidden font-mono text-xs text-fg-secondary sm:inline">
      {watching} {watching === 1 ? 'phone' : 'phones'}
    </span>
    <button
      type="button"
      onClick={onShare}
      aria-label="Share"
      className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-elevated px-2 py-1 text-xs text-fg hover:bg-bg-surface sm:px-3"
    >
      <Share2 className="size-3.5" />
      <span className="hidden sm:inline">Share</span>
    </button>
  </header>
);

export default EventHeader;
